import { useEffect, useState } from "react";
import { api, API, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../lib/context";
import { Btn } from "./Shell";
import { StatusBadge } from "./StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Upload, Camera, Eye, RotateCw, History, Repeat2, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

export function DocViewer({ doc, onClose }) {
  const [link, setLink] = useState(null);
  const [page, setPage] = useState(0);
  const [rot, setRot] = useState(doc.rotation || 0);
  useEffect(() => {
    api.post(`/documents/${doc.id}/link`).then((r) => setLink(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [doc.id]);
  const src = link && `${API}/files/${link.token}?page=${page}`;
  const isPdf = (doc.filenames?.[page] || "").toLowerCase().endsWith(".pdf");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="doc-viewer">
        <DialogHeader><DialogTitle>{doc.doc_label} · v{doc.version}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Secure link expires in 2 minutes. Every view is audit-logged.</p>
        <div className="flex max-h-[60vh] min-h-[300px] items-center justify-center overflow-auto rounded-lg bg-slate-100">
          {!src ? "Loading…" : isPdf ? <iframe title="document" src={src} className="h-[60vh] w-full" /> : <img alt={doc.doc_label} src={src} style={{ transform: `rotate(${rot}deg)` }} className="max-h-[58vh] transition-transform" />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {doc.page_count > 1 && Array.from({ length: doc.page_count }).map((_, i) => <Btn key={i} variant={i === page ? "primary" : "outline"} onClick={() => setPage(i)}>Page {i + 1}</Btn>)}
          {!isPdf && <Btn variant="outline" data-testid="doc-rotate-btn" onClick={async () => { setRot((rot + 90) % 360); await api.post(`/documents/${doc.id}/rotate`, { deg: 90 }); }}><RotateCw className="h-4 w-4" /> Rotate</Btn>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ doc, onClose, reload }) {
  const [vals, setVals] = useState(Object.fromEntries(Object.entries(doc.extracted || {}).map(([k, v]) => [k, v?.value || ""])));
  const save = async () => {
    try {
      await api.post(`/documents/${doc.id}/confirm`, { fields: vals });
      toast.success("Values confirmed");
      onClose();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="confirm-extracted-dialog">
        <DialogHeader><DialogTitle>Confirm extracted values</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Values were read automatically (OCR). OCR is not verification - please check and correct them.</p>
        {Object.keys(vals).length === 0 && <p className="text-sm">No values could be read. You can still confirm the document is correct.</p>}
        <div className="space-y-3">
          {Object.entries(vals).map(([k, v]) => (
            <div key={k}>
              <label className="mb-1 flex justify-between text-xs font-semibold uppercase text-slate-500"><span>{k.replace(/_/g, " ")}</span>
                <span className={doc.extracted[k]?.confidence >= 0.8 ? "text-emerald-700" : "text-amber-700"}>confidence {Math.round((doc.extracted[k]?.confidence || 0) * 100)}%</span></label>
              <input data-testid={`extracted-${k}`} className="min-h-[44px] w-full rounded-lg border border-slate-300 px-3 text-sm" value={v} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <Btn data-testid="confirm-extracted-btn" onClick={save}>I confirm these values</Btn>
      </DialogContent>
    </Dialog>
  );
}

function UploadForm({ g, d, reload, replace, onDone }) {
  const { config } = useAuth();
  const [type, setType] = useState(replace?.doc_type || g.alternatives[0]);
  const [subject, setSubject] = useState(replace?.subject || g.missing_people?.[0] || g.people?.[0] || "");
  const [busy, setBusy] = useState(false);
  const up = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("group_code", g.code);
    fd.append("doc_type", type);
    if (g.per_person) fd.append("subject", subject);
    if (replace) fd.append("replaces_id", replace.id);
    setBusy(true);
    try {
      await api.post(`/applications/${d.application.id}/documents`, fd);
      toast.success("Uploaded - reading document…");
      onDone?.();
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const accept = ".pdf,.jpg,.jpeg,.png";
  return (
    <div className="mt-3 grid gap-2 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/30 p-3 sm:grid-cols-[1fr_1fr_auto_auto]">
      <select aria-label="Document type" data-testid={`doc-type-${g.code}`} className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
        {g.alternatives.map((a) => <option key={a} value={a}>{config?.doc_catalogue?.[a] || a}</option>)}
      </select>
      {g.per_person ? (
        <select aria-label="Person" data-testid={`doc-subject-${g.code}`} className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-2 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)}>
          {(g.people || []).map((p) => <option key={p}>{p}</option>)}
        </select>
      ) : <span className="hidden sm:block" />}
      <label className={`flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-xl sx-grad px-4 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition-transform hover:-translate-y-0.5 ${busy ? "opacity-50" : ""}`}>
        <Upload className="h-4 w-4" /> {busy ? "Uploading…" : replace ? "Replace" : "Upload"}
        <input type="file" multiple accept={accept} className="sr-only" data-testid={`doc-file-${g.code}`} disabled={busy} onChange={(e) => up(e.target.files)} />
      </label>
      <label className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold">
        <Camera className="h-4 w-4" /> Camera
        <input type="file" accept="image/*" capture="environment" className="sr-only" disabled={busy} onChange={(e) => up(e.target.files)} />
      </label>
      <p className="col-span-full text-[11px] text-slate-500">PDF, JPG or PNG · max 8 MB per file · select several files for a multi-page document. Files are malware-scanned and stored encrypted.</p>
    </div>
  );
}

function DocRow({ doc, d, g, reload, editable }) {
  const [view, setView] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [replace, setReplace] = useState(false);
  const [hist, setHist] = useState(null);
  const owner = d.viewer_is_owner;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`doc-row-${doc.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium">{doc.doc_label}</span>
        {doc.subject && <span className="text-xs text-slate-500">· {doc.subject}</span>}
        <span className="font-mono text-[11px] text-slate-500">v{doc.version} · {doc.page_count}p</span>
        <StatusBadge status={doc.state} testid={`doc-state-${doc.id}`} />
        {doc.evidence_source?.startsWith("reused") && <span className="rounded bg-sky-50 px-2 py-0.5 text-[11px] text-sky-800">Reused with consent</span>}
        <div className="ml-auto flex flex-wrap gap-1">
          <Btn variant="ghost" data-testid={`doc-view-${doc.id}`} onClick={() => setView(true)}><Eye className="h-4 w-4" /> View</Btn>
          {owner && editable && ["extracted", "unreadable"].includes(doc.state) && <Btn variant="outline" data-testid={`doc-confirm-${doc.id}`} onClick={() => setConfirm(true)}><CheckCircle2 className="h-4 w-4" /> Confirm values</Btn>}
          {editable && doc.state !== "superseded" && <Btn variant="ghost" data-testid={`doc-replace-${doc.id}`} onClick={() => setReplace(!replace)}><Repeat2 className="h-4 w-4" /> Replace</Btn>}
          <Btn variant="ghost" data-testid={`doc-history-${doc.id}`} onClick={async () => setHist((await api.get(`/documents/${doc.id}/history`)).data)}><History className="h-4 w-4" /></Btn>
        </div>
      </div>
      {doc.state === "rejected" && <p className="mt-2 text-xs font-medium text-red-700">Rejected: {doc.review_note}. Please replace this document.</p>}
      {doc.state === "internally_reviewed" && <p className="mt-1 text-[11px] text-slate-500">Internally reviewed - not yet accepted by the bank/provider.</p>}
      {(doc.mismatch_flags || []).length > 0 && (
        <div className="mt-2 space-y-1">{doc.mismatch_flags.map((m, i) => <p key={i} className="flex items-center gap-1 text-xs text-amber-800"><AlertTriangle className="h-3 w-3" /> {m.type.replace(/_/g, " ")}: {m.extracted || m.detail} {m.expected ? `(application: ${m.expected})` : ""}</p>)}</div>
      )}
      {doc.expiry_date && <p className="mt-1 text-xs text-slate-500">Expires {doc.expiry_date}</p>}
      {replace && <UploadForm g={g} d={d} reload={reload} replace={doc} onDone={() => setReplace(false)} />}
      {hist && (
        <div className="mt-2 rounded bg-slate-50 p-2 text-xs" data-testid={`doc-history-list-${doc.id}`}>
          {hist.map((h) => <div key={h.id} className="flex gap-2"><span className="font-mono">v{h.version}</span><StatusBadge status={h.state} /><span>{fmtDate(h.created_at)}</span>{h.review_note && <span>· {h.review_note}</span>}</div>)}
        </div>
      )}
      {view && <DocViewer doc={doc} onClose={() => setView(false)} />}
      {confirm && <ConfirmDialog doc={doc} reload={reload} onClose={() => setConfirm(false)} />}
    </div>
  );
}

function ReuseList({ g, d, reload, reusable }) {
  const list = reusable.filter((x) => g.alternatives.includes(x.doc_type));
  if (!list.length || !d.viewer_is_owner) return null;
  return (
    <div className="mt-2 text-xs">
      <span className="font-semibold text-slate-600">Reuse from your profile: </span>
      {list.map((x) => (
        <button key={x.id} data-testid={`reuse-${g.code}-${x.id}`} className="ml-2 rounded border border-sky-300 bg-sky-50 px-2 py-1 text-sky-800"
          onClick={async () => { try { await api.post(`/applications/${d.application.id}/documents/reuse`, { source_doc_id: x.id, group_code: g.code }); toast.success("Reused with consent recorded"); reload(); } catch (e) { toast.error(errMsg(e)); } }}>
          {x.doc_label} ({x.state.replace(/_/g, " ")})
        </button>
      ))}
    </div>
  );
}

export default function DocumentsStep({ d, reload, editable }) {
  const [reusable, setReusable] = useState([]);
  const chk = d.checklist;
  const processing = d.documents.some((x) => x.state === "processing");
  useEffect(() => {
    api.get(`/applications/${d.application.id}/reusable-documents`).then((r) => setReusable(r.data));
  }, [d.application.id]);
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [processing, reload]);
  return (
    <div className="space-y-4" data-testid="documents-step">
      <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
        Ruleset <span className="font-mono">{chk.ruleset_id} v{chk.ruleset_version}</span> · {chk.approved_source}
        {chk.approval_status !== "approved" && <span className="ml-2 font-semibold text-amber-700">({chk.approval_status?.replace(/_/g, " ")})</span>}
      </div>
      {chk.groups.map((g) => (
        <div key={g.code} className={`rounded-2xl border p-5 transition-all duration-200 ${g.satisfied ? "border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white" : g.required ? "border-slate-200 bg-white shadow-sm" : "border-dashed border-slate-200 bg-white"}`} data-testid={`checklist-${g.code}`}>
          <div className="flex flex-wrap items-center gap-2">
            {g.satisfied ? <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-label="Satisfied" /> : <AlertTriangle className={`h-5 w-5 ${g.required ? "text-amber-600" : "text-slate-400"}`} aria-label="Missing" />}
            <span className="font-semibold">{g.name}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${g.required ? "text-red-600" : "text-slate-400"}`}>{g.required ? "Required" : "Optional"}</span>
            {g.min > 1 && <span className="text-xs text-slate-500">({g.count}/{g.min} proofs)</span>}
            <span className="ml-auto text-xs font-semibold" data-testid={`checklist-status-${g.code}`}>{g.satisfied ? "Complete" : "Pending"}</span>
          </div>
          <p className="mt-1 text-sm text-slate-600"><b>Why:</b> {g.why}</p>
          <p className="text-xs text-slate-500"><b>Accepted alternatives:</b> {g.alternatives.length} option(s) - choose one in the upload type list{g.max_age_days ? ` · must be dated within ${g.max_age_days} days` : ""}</p>
          {g.per_person && <p className="text-xs text-slate-500">Needed for each signatory: {(g.people || []).join(", ") || "add signatories in the stakeholder step"}{g.missing_people?.length ? ` · missing: ${g.missing_people.join(", ")}` : ""}</p>}
          <div className="mt-3 space-y-2">{d.documents.filter((x) => x.group_code === g.code && x.state !== "superseded").map((doc) => <DocRow key={doc.id} doc={doc} d={d} g={g} reload={reload} editable={editable} />)}</div>
          {editable && <UploadForm g={g} d={d} reload={reload} />}
          {editable && <ReuseList g={g} d={d} reload={reload} reusable={reusable} />}
        </div>
      ))}
    </div>
  );
}
